import { Add, ArrowUpward, Delete, DirectionsCar } from '@mui/icons-material'
import Box from '@mui/material/Box'
import { I18n, Translate2 as Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import miscellaneousVehicle from 'src/features/search/Results/utils/miscellaneousVehicle'
import { CartMode } from 'src/store/models/ShoppingCartModels'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { UserAttributeKey } from 'src/store/user/interfaces'
import { useUserStore } from 'src/store/user/UserStore'
import { breakpoints } from 'src/theme/breakpoints'
import { Button, Price, SimpleModal } from 'src/ui-components'
import styled from 'styled-components'
import { defaultTheme } from '../../../../src/theme/index'
import { SellerCartVehicle } from '../store/ISellerCart'

import { show as showNiceModal } from '@ebay/nice-modal-react'
import { Divider } from '@mui/material'
import {
  AvailabilityErrorType,
  NonCachedSearchType,
  ProductModel,
} from 'interfaces'
import { useQuoteStore } from 'pages/quotes/src/store/QuoteStore'
import { useNavigate } from 'react-router-dom'
import { EmbeddedCommunicationsManager } from 'src/api/embedded/EmbeddedCommunicationsManager'
import {
  IByPartPayLoad,
  ImageBypartAPIResponse,
} from 'src/api/searchResults/interfaces'
import { IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { CartPriceChangedProductsModal } from './CartPriceChangedProductsModal'
import { getCxmlDetails } from 'src/helpers/cxmlDetailsUtil'
import PartExtServiceProvider from 'src/services/PartExtServiceProvider'
import { usePartsCatalog } from 'src/store/partsCatalog/PartsCatalog'
import { useSearchStore } from 'src/store/search/SearchStore'
import {
  DuplicateVehicleActions,
  ToastNotificationVariant,
} from '../../../../src/ui-components/util/Interfaces'
import { CartLaborItems } from '../../../../src/features/cart/components/LaborCartItems/CartLaborItems'
import { CartSpecificationItems } from '../../../../src/features/cart/components/SpecificationCartItems/CartSpecificationItems'
import { useSelector } from 'react-redux'
import { RootState, store, useAppDispatch } from 'store'
import {
  getCartLaborCost,
  getVehicleOrderFormData,
  getVehiclePartsCoreCost,
  getVehiclePartsTotalCost,
} from '../store/cartSelectors'
import { cartSlice, setMultiCartValidated } from '../store/cartStore'
import { getCartMode } from '../Utils'
import { OrderFormValidationException } from '../exceptions/OrderFormValidationException'
import { timeout } from 'src/theme/timeout'
import { setOrderResponses } from 'orderConfirmation'
import {
  getVehicleProductsErrors,
  isSameVehicle,
} from '../utils/Utils_testable'
import { validateSingleCartPricesAndQty } from '../store/storeThunks'
import { GaTrackOption } from 'src/config/analytics/tracking/utils'
import {
  buildPartsWithVehicleInfo,
  CartActionType,
} from 'src/config/analytics/UserActionsManager'

import { TrackingManager } from 'src/config/analytics/tracking/core'
import { CofirmationModal } from 'src/ui-components/modals/ConfirmationModal'
import { formatVehicleDisplayWithOutEngine } from 'src/helpers/utils'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { MainCartOrderForm } from './MainCartOrderForm'
import { SellerCartOrderForm } from './SellerCartOrderForm'
import { SellerInfo } from './SellerInfo'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { getInvalidVehicles } from '../utils/Utils'
import { placeOrder } from '../utils/orderUtils'
import { OrderTimeoutError } from './OrderTimeoutError'

interface SellerCartViewProps {
  sellerCartVehicle: SellerCartVehicle
}

export const SellerCartView = observer(
  ({ sellerCartVehicle }: SellerCartViewProps): ReactElement => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { isMultisellerUser } = useUiStateStore()
    const expanded = true
    const currentVehicle = useMemo(
      () => sellerCartVehicle.vehicle ?? {},
      [sellerCartVehicle.vehicle]
    )
    const getCartName = (): string => {
      if (currentVehicle?.year?.id === miscellaneousVehicle?.year?.id)
        return 'miscellaneous'

      if (currentVehicle?.engine?.id) {
        return formatVehicleDisplayWithOutEngine(currentVehicle)
      }

      return ''
    }

    const vehicleStr = getCartName()
    const quoteSourceId = useMemo(
      () => sellerCartVehicle?.quoteSourceId ?? null,
      [sellerCartVehicle?.quoteSourceId]
    )

    const userStore = useUserStore()
    const { saveMultiSellerQuote, updateMultiSellerQuote, saveAsQuoteLoading } =
      useQuoteStore()
    const theQuoteWasSavedSuccessfullyMsg = Translate(
      'theQuoteWasSavedSuccessfully'
    )

    const displayExclTax = userStore?.preferences?.displayExclTax === 'true'
    const { getUserAttribute } = userStore
    const { isAstPunchOut } = EmbeddedCommunicationsManager
    const { cxmlData } = getCxmlDetails()

    const [partImages, setPartImages] = useState<ImageBypartAPIResponse[]>([])

    const { isMultiCartValidated, costSubTotal, coreSubTotal, laborSubTotal } =
      useSelector((state: RootState) => ({
        isMultiCartValidated: state.cart.isMultiCartValidated,
        costSubTotal: getVehiclePartsTotalCost(
          state,
          sellerCartVehicle.vehicle
        ),
        coreSubTotal: getVehiclePartsCoreCost(state, sellerCartVehicle.vehicle),
        laborSubTotal: getCartLaborCost(state, sellerCartVehicle.vehicle),
      }))

    const cartPrice = costSubTotal + coreSubTotal + laborSubTotal

    const displayLabourCost =
      getUserAttribute(UserAttributeKey.epeFeatures_labor) === 'true'

    const processingOrder = useSelector(
      (state: RootState) => state.ui.processingOrder
    )

    const isSamePoNumber = useSelector(
      (state: RootState) =>
        !!getVehicleOrderFormData(state, currentVehicle).poNumber &&
        // isSamePoNumber is only for multi seller functionality.
        isMultisellerUser()
    )

    const [showConfirmEmpty, setShowConfirmEmpty] = useState(false)
    const [showConfirmRemove, setShowConfirmRemove] = useState(false)
    const emptyCartNotificationMsg = Translate('yourCartWasSuccessfullyEmptied')

    const { updateCurrentVehicle } = useSearchStore()
    const { resetStore } = usePartsCatalog()

    const [isSingleCartValidated, setIsSingleCartValidated] =
      useState<boolean>(false)
    // tracks whether validation found silent errors (e.g. cant-be-fulfilled)
    // used to show the yellow warning bar and change button text to 'proceedWithOrder'
    const [hasValidationFailed, setHasValidationFailed] =
      useState<boolean>(false)
    const isValidatingCartRef = useRef(false)
    const timeoutRetryCountRef = useRef(0)

    useEffect(() => {
      const sellerLocationParts: ProductModel[] = []

      sellerCartVehicle.sellers.forEach((s) =>
        s.locations?.map((l) =>
          l.parts?.map((p) => sellerLocationParts.push(p))
        )
      )

      const buildByPartPayload: IByPartPayLoad[] = sellerLocationParts.map(
        (part) => ({ partNumber: part.partNumber, lineCode: part.lineCode })
      )
      PartExtServiceProvider.getPartAttributesByPart(buildByPartPayload).then(
        (resp) => {
          if (resp.length) setPartImages(resp)
        }
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDismiss = {
      text: Translate('dismiss'),
      action: () => {
        //
      },
    }

    const placeSellerCartOrder = async () => {
      try {
        const response = await placeOrder([sellerCartVehicle])

        if (response && getCartMode() === CartMode.CART_ONLY) return

        if (response) {
          setOrderResponses(response)
          store.dispatch(
            cartSlice.actions.removeVehicleFromCart({
              vehicle: sellerCartVehicle.vehicle,
              track: GaTrackOption.doNotTrack,
            })
          )

          navigate('/orderConfirmation')
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (e instanceof OrderFormValidationException) {
          ShowNotification({
            message: e.message,
            variant: ToastNotificationVariant.ERROR,
          })
        } else {
          ShowNotification({
            message: 'unableToPlaceOrderPleaseContactTheAdministrator',
            variant: ToastNotificationVariant.ERROR,
            link: handleDismiss,
            time: timeout.orderErrorNotification,
          })
        }
      }
    }

    const handleOnClickSaveAsQuote = async () => {
      try {
        await saveMultiSellerQuote(sellerCartVehicle).then(() => {
          const successMessage =
            currentVehicle?.engine?.id && quoteSourceId
              ? Translate('wasSuccessfullyUpdate', [quoteSourceId || ''])
              : theQuoteWasSavedSuccessfullyMsg

          ShowNotification({
            message: successMessage,
            variant: ToastNotificationVariant.SUCCESS,
            link: {
              text: Translate('viewQuotes'),
              action: () => navigate('/quotes'),
            },
          })
        })

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
      } catch (error: any) {
        ShowNotification({
          message: JSON.stringify(error?.message),
          variant: ToastNotificationVariant.ERROR,
        })
      }
    }

    const handleUpdateQuote = async () => {
      try {
        await updateMultiSellerQuote(sellerCartVehicle).then(() => {
          const successMessage = Translate('wasSuccessfullyUpdate', [
            quoteSourceId || '',
          ])

          store.dispatch(
            cartSlice.actions.removeVehicleFromCart({
              vehicle: sellerCartVehicle.vehicle,
              track: GaTrackOption.doNotTrack,
            })
          )

          ShowNotification({
            message: successMessage,
            variant: ToastNotificationVariant.SUCCESS,
            link: {
              text: Translate('viewQuotes'),
              action: () => navigate('/quotes'),
            },
          })
        })

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
      } catch (error: any) {
        ShowNotification({
          message: JSON.stringify(error?.message),
          variant: ToastNotificationVariant.ERROR,
        })
      }
    }

    const handleUpdateQuoteConfirmation = (): void => {
      showNiceModal(CofirmationModal, {
        title: 'updateQuote',
        primaryActionText: 'updateQuote',
        secondaryActionText: 'cancel',
        children: (
          <>
            <I18n name="areYouSureYouWantToUpdate" />{' '}
            <b>{Translate('quoteNumber', [quoteSourceId])}</b>?{' '}
            <I18n name="thisCannotBeUndone" />
          </>
        ),
      }).then((data) => {
        if (data) {
          handleUpdateQuote()
        }
      })
    }

    const handleAddParts = async () => {
      if (isAstPunchOut) {
        StoreInstances.astPunchOutStore.catalog.resetStore()
        navigate('/astPartsCatalog')
        return
      }

      const lastSearch = StoreInstances.searchStore.lastNonCachedSearch
      if (
        IS_INTEGRATED_VERSION &&
        lastSearch === NonCachedSearchType.searchByAstNavigation
      ) {
        StoreInstances.astPunchOutStore.catalog.resetStore()
        navigate('/partsCatalog')
        return
      }

      await updateCurrentVehicle(currentVehicle, DuplicateVehicleActions.MERGE)
      resetStore()
      navigate('/partsCatalog')
    }

    const validateCartAvailabilityErrors = async (): Promise<void> => {
      if (isValidatingCartRef.current) return
      isValidatingCartRef.current = true
      dispatch(cartSlice.actions.setDisplayCartFormErrors(true))
      const invalidVehicles = getInvalidVehicles([sellerCartVehicle])
      if (invalidVehicles.length) {
        ShowNotification({
          message: 'orderFormIsIncomplete',
          variant: ToastNotificationVariant.ERROR,
        })
        isValidatingCartRef.current = false
        return
      }

      if (isSingleCartValidated || isMultiCartValidated) {
        placeSellerCartOrder()
        return
      }

      try {
        const { hasSameCartErrors, vehiclePriceChangeErrors, hasTimeout } =
          await dispatch(
            validateSingleCartPricesAndQty(sellerCartVehicle)
          ).unwrap()

        if (hasTimeout) {
          timeoutRetryCountRef.current += 1
          if (timeoutRetryCountRef.current >= 2) {
            // Second timeout - show timeout modal
            showNiceModal(OrderTimeoutError, {
              onTryAgain: () => {
                timeoutRetryCountRef.current = 0
                setIsSingleCartValidated(false)
                dispatch(setMultiCartValidated({ value: false }))
                isValidatingCartRef.current = false
                validateCartAvailabilityErrors()
              },
              onSaveAsQuote: () => {
                handleOnClickSaveAsQuote()
              },
            })
          } else {
            // First timeout - show toast and auto-retry
            ShowNotification({
              message: 'itsTakingLongerThanUsualToPerformYourInquiry',
              variant: ToastNotificationVariant.ERROR,
            })
            isValidatingCartRef.current = false
            validateCartAvailabilityErrors()
          }
          return
        }

        // Reset retry count on success
        timeoutRetryCountRef.current = 0
        setIsSingleCartValidated(true)
        dispatch(setMultiCartValidated({ value: true }))

        if (hasSameCartErrors) {
          placeSellerCartOrder()
          return
        }

        // read from store.getState() instead of the sellerCartVehicle prop
        // because the prop is a stale snapshot — errors are written to Redux
        // during validation and won't be reflected in the prop until re-render
        const insufficientQtyWarning =
          userStore?.preferences?.insufficientQtyWarning === 'true'
        const hasSilentErrors = getVehicleProductsErrors(
          store
            .getState()
            .cart.cartData.vehicles.find((v) =>
              isSameVehicle(v.vehicle, sellerCartVehicle.vehicle)
            ) ?? sellerCartVehicle
        ).some((p) =>
          p.product.errors?.some(
            (e) =>
              e.errorType !== AvailabilityErrorType.PRICE_OVERRIDE &&
              (e.errorType !== AvailabilityErrorType.INSUFFICIENT_QTY ||
                insufficientQtyWarning)
          )
        )
        if (hasSilentErrors || vehiclePriceChangeErrors?.length)
          setHasValidationFailed(true)

        if (vehiclePriceChangeErrors?.length) {
          showNiceModal(CartPriceChangedProductsModal, {
            title: 'importantMessageAboutItemsInYourCart',
            primaryButtonText: 'confirmOrder',
            secondaryButtonText: 'returnToCart',
            vehiclePriceChangeErrors,
            mainSellerVehicleData: [sellerCartVehicle],
          })
          return
        }

        // optional: auto place order if no errors at all
        // placeSellerCartOrder()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Cart validation failed', error)
      } finally {
        isValidatingCartRef.current = false
      }
    }

    return (
      <Wrapper
        id={`vehicle_${StoreInstances.searchStore.getVehicleHash(currentVehicle)}`}
      >
        {getCartMode() !== CartMode.CART_ONLY && (
          <CartHeader>
            <VehicleHeaderContainer>
              <VehicleInfoContainer>
                {vehicleStr && (
                  <Box display="flex">
                    <DirectionsCar
                      fontSize="large"
                      style={{ marginRight: 6 }}
                    />
                    <VehicleInfo>
                      <StyledInfoTextUppercase bold>
                        <I18n name={vehicleStr} />
                      </StyledInfoTextUppercase>
                      <VehicleEngineText>
                        {currentVehicle.engine?.value}
                      </VehicleEngineText>
                    </VehicleInfo>
                  </Box>
                )}
              </VehicleInfoContainer>
              {expanded ? (
                <>
                  <Hr />
                  <VehicleActions>
                    <AddParts onClick={handleAddParts}>
                      <Add />
                      <I18n name="addParts" />
                    </AddParts>
                    {import.meta.env.VITE_FEATURE_QUOTES_ENABLED === 'true' &&
                      (quoteSourceId ? (
                        <Button
                          variant="secondary"
                          text="updateQuote"
                          onClick={() => handleUpdateQuoteConfirmation()}
                        />
                      ) : (
                        <Button
                          loading={saveAsQuoteLoading}
                          variant="secondary"
                          text="saveAsQuote"
                          onClick={() => handleOnClickSaveAsQuote()}
                        />
                      ))}
                    {getCartMode() === CartMode.MULTI_VEHICLE && (
                      <Button
                        onClick={() => setShowConfirmRemove(true)}
                        variant="danger"
                        text="removeCart"
                      />
                    )}
                    {getCartMode() === CartMode.SINGLE_VEHICLE && (
                      <Button
                        onClick={() => setShowConfirmEmpty(true)}
                        variant="danger"
                        text="emptyCart"
                      />
                    )}
                  </VehicleActions>
                  <Hr />
                </>
              ) : null}
            </VehicleHeaderContainer>
          </CartHeader>
        )}

        <CartTableWrapper>
          {expanded && (
            <>
              {isMultisellerUser() && (
                <SellerCartOrderForm currentVehicle={currentVehicle} />
              )}

              <VehicleSellerInfoWrapper>
                {sellerCartVehicle.sellers.length > 0 && (
                  <>
                    {/* show multi seller names here */}
                    {sellerCartVehicle.sellers.map((seller) => {
                      return seller.locations?.flatMap((l) => l.parts).length >
                        0 ? (
                        isMultisellerUser() ? (
                          <SellerInfo
                            key={seller.coverageKey}
                            partImages={partImages}
                            currentVehicle={currentVehicle}
                            seller={seller}
                            IsSamePoNumber={isSamePoNumber}
                          />
                        ) : (
                          <MainCartOrderForm
                            key={seller.coverageKey}
                            partImages={partImages}
                            currentVehicle={currentVehicle}
                            seller={seller}
                            IsSamePoNumber={isSamePoNumber}
                          />
                        )
                      ) : null
                    })}
                  </>
                )}
              </VehicleSellerInfoWrapper>

              <CartSpecificationItems currentVehicle={currentVehicle} />
              <CartLaborItems currentVehicle={currentVehicle} />
            </>
          )}
          {/* shown after validation finds silent errors (e.g. cant-be-fulfilled parts) */}
          {hasValidationFailed && (
            <>
              <ValidationWarningDivider />
              <ValidationWarningBar>
                <span
                  className="material-icons md-dark text-warning"
                  style={{
                    marginRight: '8px',
                    fontSize: '16px',
                    flexShrink: 0,
                  }}
                >
                  warning
                </span>
                <ValidationWarningTitle>
                  <I18n name="orderExceptionsMessage" />
                </ValidationWarningTitle>
                <ValidationWarningSubText>
                  <I18n name="orderExceptionsSubMessage" />
                </ValidationWarningSubText>
                <ValidationWarningLink
                  onClick={() => {
                    // try to scroll to the first error row in this vehicle's cart
                    // falls back to the vehicle-errors div if no specific row found
                    const vehicleWrapper = document.getElementById(
                      `vehicle_${StoreInstances.searchStore.getVehicleHash(currentVehicle)}`
                    )
                    const firstErrorRow =
                      vehicleWrapper?.querySelector<HTMLElement>(
                        '[data-has-error="true"]'
                      )
                    const fallback = document.getElementById(
                      `vehicle-errors-${currentVehicle.id}`
                    )
                    ;(firstErrorRow ?? fallback)?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    })
                  }}
                >
                  <I18n name="review" />
                  &nbsp;
                  <ArrowUpward
                    style={{ fontSize: '16px', verticalAlign: 'middle' }}
                  />
                </ValidationWarningLink>
              </ValidationWarningBar>
            </>
          )}
          <Footer>
            <PriceWrapper>
              <I18n name="parts" />: &nbsp;
              <StyledPrice>
                <Price num={costSubTotal} />
              </StyledPrice>
            </PriceWrapper>
            <PriceWrapper>
              <I18n name="core" />: &nbsp;
              <StyledPrice>
                <Price num={coreSubTotal} />
              </StyledPrice>
            </PriceWrapper>
            {displayLabourCost && (
              <PriceWrapper>
                <I18n name="labor" />: &nbsp;
                <StyledPrice>
                  <Price num={laborSubTotal} />
                </StyledPrice>
              </PriceWrapper>
            )}
            <DividerLine orientation="vertical" flexItem light />
            <CartTotalWrapper>
              <CartTotalPriceWrapper>
                <I18n name="cartTotal" />: &nbsp;
                <Price num={cartPrice} />
              </CartTotalPriceWrapper>
              {displayExclTax && (
                <ExclTaxText>
                  <I18n name="displayExclTax" />
                </ExclTaxText>
              )}
            </CartTotalWrapper>
            {getCartMode() === CartMode.MULTI_VEHICLE &&
              !isAstPunchOut &&
              !cxmlData && (
                <Button
                  text={
                    hasValidationFailed ? 'proceedWithOrder' : 'orderThisCart'
                  }
                  type="submit"
                  disabled={
                    sellerCartVehicle?.sellers.length <= 0 || processingOrder
                  }
                  onClick={() => {
                    validateCartAvailabilityErrors()
                  }}
                  loading={processingOrder}
                />
              )}
          </Footer>
        </CartTableWrapper>

        <SimpleModal
          show={showConfirmRemove}
          title={Translate('removeVehicleCart', [vehicleStr])}
          onSecondaryAction={() => setShowConfirmRemove(false)}
          onPrimaryAction={() => {
            const allParts: ProductModel[] = []
            sellerCartVehicle.sellers.forEach((s) =>
              s.locations?.forEach((l) =>
                l.parts?.forEach((p) => allParts.push(p))
              )
            )

            if (allParts.length > 0) {
              const partsWithVehicle = buildPartsWithVehicleInfo({
                ...sellerCartVehicle.vehicle,
                parts: allParts,
              })
              TrackingManager.trackEvent(CartActionType.cartDelete, {
                parts: partsWithVehicle,
              })
            }

            store.dispatch(
              cartSlice.actions.removeVehicleFromCart({
                vehicle: sellerCartVehicle.vehicle,
                track: GaTrackOption.track,
              })
            )
            setShowConfirmRemove(false)
            ShowNotification({
              message: emptyCartNotificationMsg,
              variant: ToastNotificationVariant.SUCCESS,
            })
          }}
          secondaryActionText="cancel"
          primaryActionText="removeCart"
          primaryButtonVariant="danger"
          primaryButtonIcon={<Delete />}
          primaryButtonToTheLeft
        >
          <>
            <I18n name="areYouSureYouWantTo" />
            &nbsp;
            <b>
              <I18n name="removeYour" />
            </b>
            &nbsp;
            <b>{vehicleStr}&nbsp;</b>
            <LowerCase>
              <I18n name="cart" />?
            </LowerCase>
            <br />
            <I18n name="thisWillRemoveAllItemsFromTheCartAndCannotBeUndone" />
          </>
        </SimpleModal>
        <SimpleModal
          show={showConfirmEmpty}
          title="emptyCart"
          onSecondaryAction={() => setShowConfirmEmpty(false)}
          onPrimaryAction={() => {
            const allParts: ProductModel[] = []
            sellerCartVehicle.sellers.forEach((s) =>
              s.locations?.forEach((l) =>
                l.parts?.forEach((p) => allParts.push(p))
              )
            )

            if (allParts.length > 0) {
              const partsWithVehicle = buildPartsWithVehicleInfo({
                ...sellerCartVehicle.vehicle,
                parts: allParts,
              })
              TrackingManager.trackEvent(CartActionType.cartDelete, {
                parts: partsWithVehicle,
              })
            }

            store.dispatch(
              cartSlice.actions.removeVehicleFromCart({
                vehicle: sellerCartVehicle.vehicle,
                track: GaTrackOption.track,
              })
            )
            setShowConfirmEmpty(false)
            ShowNotification({
              message: emptyCartNotificationMsg,
              variant: ToastNotificationVariant.SUCCESS,
            })
          }}
          secondaryActionText="cancel"
          primaryActionText="emptyCart"
          primaryButtonVariant="danger"
        >
          <>
            <I18n name="areYouSureYouWantToEmptyYourCart" />
            <br />
            <I18n name="thisWillRemoveAllItemsFromTheCartAndCannotBeUndone" />
          </>
        </SimpleModal>
        <SimpleModal
          show={showConfirmRemove}
          title={Translate('removeVehicleCart', [vehicleStr])}
          onSecondaryAction={() => setShowConfirmRemove(false)}
          onPrimaryAction={() => {
            const allParts: ProductModel[] = []
            sellerCartVehicle.sellers.forEach((s) =>
              s.locations?.forEach((l) =>
                l.parts?.forEach((p) => allParts.push(p))
              )
            )

            if (allParts.length > 0) {
              const partsWithVehicle = buildPartsWithVehicleInfo({
                ...sellerCartVehicle.vehicle,
                parts: allParts,
              })
              TrackingManager.trackEvent(CartActionType.cartDelete, {
                parts: partsWithVehicle,
              })
            }

            store.dispatch(
              cartSlice.actions.removeVehicleFromCart({
                vehicle: sellerCartVehicle.vehicle,
                track: GaTrackOption.track,
              })
            )
            setShowConfirmRemove(false)
            ShowNotification({
              message: emptyCartNotificationMsg,
              variant: ToastNotificationVariant.SUCCESS,
            })
          }}
          secondaryActionText="cancel"
          primaryActionText="removeCart"
          primaryButtonVariant="danger"
          primaryButtonIcon={<Delete />}
          primaryButtonToTheLeft
        >
          <>
            <I18n name="areYouSureYouWantTo" />
            &nbsp;
            <b>
              <I18n name="removeYour" />
            </b>
            &nbsp;
            <b>{vehicleStr}&nbsp;</b>
            <LowerCase>
              <I18n name="cart" />?
            </LowerCase>
            <br />
            <I18n name="thisWillRemoveAllItemsFromTheCartAndCannotBeUndone" />
          </>
        </SimpleModal>
        <SimpleModal
          show={showConfirmEmpty}
          title="emptyCart"
          onSecondaryAction={() => setShowConfirmEmpty(false)}
          onPrimaryAction={(): void => {
            const allParts: ProductModel[] = []
            sellerCartVehicle.sellers.forEach((s) =>
              s.locations?.forEach((l) =>
                l.parts?.forEach((p) => allParts.push(p))
              )
            )

            if (allParts.length > 0) {
              const partsWithVehicle = buildPartsWithVehicleInfo({
                ...sellerCartVehicle.vehicle,
                parts: allParts,
              })
              TrackingManager.trackEvent(CartActionType.cartDelete, {
                parts: partsWithVehicle,
              })
            }

            store.dispatch(
              cartSlice.actions.removeVehicleFromCart({
                vehicle: sellerCartVehicle.vehicle,
                track: GaTrackOption.track,
              })
            )
            setShowConfirmEmpty(false)
            ShowNotification({
              message: emptyCartNotificationMsg,
              variant: ToastNotificationVariant.SUCCESS,
            })
          }}
          secondaryActionText="cancel"
          primaryActionText="emptyCart"
          primaryButtonVariant="danger"
        >
          <>
            <I18n name="areYouSureYouWantToEmptyYourCart" />
            <br />
            <I18n name="thisWillRemoveAllItemsFromTheCartAndCannotBeUndone" />
          </>
        </SimpleModal>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  margin-bottom: 20px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;

  @media (max-width: ${breakpoints.laptopMedium - 1}px) {
    margin-bottom: 12px;
  }
`

const CartHeader = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
  background-color: ${defaultTheme.colors.componentsBackground};
  padding: 12px 20px 12px 20px;
  margin-bottom: 3px;
  font-size: 16px;
  font-family: Oswald, serif;
  font-weight: bold;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  @media (max-width: 1200px) {
    margin-bottom: 0;
    padding: 12px;
  }
`

const DividerLine = styled(Divider)`
  margin-left: 5px;
  margin-right: 10px;
  background-color: #ffffff !important;
  width: 4px;
  height: 40px !important;
  opacity: 1;
  align-self: center;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`

const VehicleHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  gap: 12px;
  min-width: 0;
  @media (max-width: ${breakpoints.laptop}px) and (min-width: ${breakpoints.tablet +
    1}px) {
    flex-direction: column;
    align-items: stretch;
  }
`
const VehicleInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  @media (max-width: ${breakpoints.tablet}px) {
    justify-content: space-between;
  }
`

const VehicleInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    align-items: start;
  }
`

interface StyledInfoTextProps {
  bold?: boolean
}
const StyledInfoText = styled.div<StyledInfoTextProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 10px;
  font-family: Roboto, serif;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  font-size: 14px;
  @media (max-width: ${breakpoints.laptop}px) {
    font-size: 16px;
    font-family: Oswald, serif;
  }
`

const StyledInfoTextUppercase = styled(StyledInfoText)`
  text-transform: uppercase;
`

const VehicleEngineText = styled.div<StyledInfoTextProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 10px;
  font-family: Roboto, serif;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  font-size: 14px;
`

export const Hr = styled.hr`
  display: none;
  @media (max-width: ${breakpoints.laptop}px) and (min-width: ${breakpoints.tablet +
    1}px) {
    display: block;
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    margin-bottom: 10px;
    border: 1px solid ${defaultTheme.colors.border};
  }
`

const VehicleActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  min-width: 0;
  max-width: 100%;

  @media (max-width: ${breakpoints.laptop}px) and (min-width: ${breakpoints.tablet +
    1}px) {
    width: 100%;
    justify-content: flex-start;
  }
`

const AddParts = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding-right: 16px;
  font-size: 18px;
  white-space: nowrap;
  @media (max-width: ${breakpoints.tablet}px) {
    font-size: 14px;
    padding-right: 8px;
  }
`

const CartTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${defaultTheme.colors.componentsBackground};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
`

export const CartSubtitle = styled.h4`
  font-family: 'Oswald', serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: ${defaultTheme.colors.text};
  mix-blend-mode: normal;
  margin: 0;
  text-align: left;
`

export const CartTable = styled.table`
  width: 100%;
`

export const HeaderRow = styled.thead`
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  border-bottom: 1px solid ${defaultTheme.colors.border};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

export const ColHeader = styled.td`
  background-color: ${defaultTheme.colors.white};
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  padding: 20px 14px 20px 14px;
  text-align: left;
`

const Footer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 20px 10px 20px;
  background-color: ${defaultTheme.colors.primaryText};
  color: ${defaultTheme.colors.secondary};
  font-family: Oswald, serif;
  font-weight: bold;
  font-size: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    flex-direction: column;
    align-items: stretch;
  }
  @media (min-width: ${breakpoints.tablet}px) and (max-width: ${breakpoints.laptop}px) {
    justify-content: space-between;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  margin-right: 11px;
  align-items: center;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`

const StyledPrice = styled.span`
  font-weight: 500;
`

const CartTotalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 11px;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    margin-right: 0;
    width: 100%;
  }
`

const CartTotalPriceWrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    width: 100%;
    justify-content: space-between;
  }
`

const ExclTaxText = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.secondary};
`

const VehicleSellerInfoWrapper = styled.div`
  margin: 0 20px;
  max-width: 100%;
  min-width: 0;

  @media (max-width: ${breakpoints.tablet}px) {
    margin: 0 8px;
  }
`

const LowerCase = styled.span`
  text-transform: lowercase;
`

const ValidationWarningDivider = styled.div`
  height: 2px;
  background-color: ${defaultTheme.colors.primary};
  width: 100%;
  margin-top: 8px;
`

const ValidationWarningBar = styled.div`
  background-color: ${defaultTheme.colors.yellowLight};
  padding: 18px 20px;
  margin-top: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`

const ValidationWarningTitle = styled.span`
  font-weight: 700;
  font-size: 16px;
  font-family: Roboto, serif;
  color: ${defaultTheme.colors.primaryText};
  white-space: nowrap;
  margin-right: 6px;
`

const ValidationWarningSubText = styled.span`
  font-size: 16px;
  font-family: Roboto, serif;
  color: ${defaultTheme.colors.primaryText};
  flex: 1;
`

const ValidationWarningLink = styled.span`
  font-size: 16px;
  font-family: Roboto, serif;
  color: ${defaultTheme.colors.link};
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  margin-left: 8px;
`
